import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Card } from "@/shared/components/ui/Card"
import {
  FormField,
  FormSection,
  NumericInput,
  TextAreaField,
  DateTimeField,
  ImageUploadField,
} from "@/shared/components/forms"
import { waterQualitySchema, type WaterQualityFormData } from "../schemas/waterQualitySchema"

interface WaterQualityFormProps {
  defaultValues?: Partial<WaterQualityFormData>
  onSubmit: (data: WaterQualityFormData) => Promise<void>
  isLoading: boolean
}

/**
 * Form for creating/editing water quality tests.
 * Uses FormSection for progressive disclosure of optional fields.
 */
export function WaterQualityForm({ defaultValues, onSubmit, isLoading }: WaterQualityFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<WaterQualityFormData>({
    resolver: zodResolver(waterQualitySchema),
    defaultValues: {
      dissolvedOxygen: undefined,
      ph: undefined,
      temperature: undefined,
      testedAt: new Date(),
      salinity: undefined,
      ammonia: undefined,
      nitrite: undefined,
      nitrate: undefined,
      turbidity: undefined,
      notes: "",
      imageIds: [],
      ...defaultValues,
    },
  })

  const notes = watch("notes") || ""

  // Count errors in each section for badges
  const essentialErrors = [errors.dissolvedOxygen, errors.ph, errors.temperature, errors.testedAt].filter(Boolean).length
  const nitrogenErrors = [errors.ammonia, errors.nitrite, errors.nitrate].filter(Boolean).length
  const additionalErrors = [errors.salinity, errors.turbidity].filter(Boolean).length
  const notesErrors = [errors.notes, errors.imageIds].filter(Boolean).length

  const onFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data)
  })

  return (
    <form id="water-quality-form" onSubmit={onFormSubmit} className="space-y-4">
      {/* Essential Parameters - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Essential Parameters" defaultExpanded={true} errorCount={essentialErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Dissolved Oxygen" required error={errors.dissolvedOxygen}>
              <NumericInput
                {...register("dissolvedOxygen", { valueAsNumber: true })}
                unit="mg/L"
                placeholder="0.0"
                hasError={!!errors.dissolvedOxygen}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="pH" required error={errors.ph}>
              <NumericInput
                {...register("ph", { valueAsNumber: true })}
                placeholder="7.0"
                hasError={!!errors.ph}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Temperature" required error={errors.temperature}>
              <NumericInput
                {...register("temperature", { valueAsNumber: true })}
                unit="°C"
                placeholder="25.0"
                hasError={!!errors.temperature}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Test Date & Time" required error={errors.testedAt}>
              <DateTimeField
                name="testedAt"
                control={control}
                showTime={true}
                maxDate={new Date()}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Nitrogen Compounds - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Nitrogen Compounds" defaultExpanded={false} errorCount={nitrogenErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Ammonia" error={errors.ammonia} hint="Optional">
              <NumericInput
                {...register("ammonia", { valueAsNumber: true })}
                unit="mg/L"
                placeholder="0.0"
                hasError={!!errors.ammonia}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Nitrite" error={errors.nitrite} hint="Optional">
              <NumericInput
                {...register("nitrite", { valueAsNumber: true })}
                unit="mg/L"
                placeholder="0.0"
                hasError={!!errors.nitrite}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Nitrate" error={errors.nitrate} hint="Optional">
              <NumericInput
                {...register("nitrate", { valueAsNumber: true })}
                unit="mg/L"
                placeholder="0.0"
                hasError={!!errors.nitrate}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Additional Parameters - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Additional Parameters" defaultExpanded={false} errorCount={additionalErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Salinity" error={errors.salinity} hint="Optional">
              <NumericInput
                {...register("salinity", { valueAsNumber: true })}
                unit="ppt"
                placeholder="0.0"
                hasError={!!errors.salinity}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Turbidity" error={errors.turbidity} hint="Optional">
              <NumericInput
                {...register("turbidity", { valueAsNumber: true })}
                unit="NTU"
                placeholder="0.0"
                hasError={!!errors.turbidity}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Notes & Images - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Notes & Images" defaultExpanded={false} errorCount={notesErrors}>
          <div className="space-y-4">
            <FormField label="Notes" error={errors.notes} hint="Optional observations or comments">
              <TextAreaField
                {...register("notes")}
                placeholder="Add any observations, weather conditions, or notes about this test..."
                maxLength={2000}
                currentLength={notes.length}
                rows={4}
                hasError={!!errors.notes}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Images" error={errors.imageIds} hint="Up to 5 images, max 10MB each">
              <Controller
                name="imageIds"
                control={control}
                render={({ field }) => (
                  <ImageUploadField
                    value={field.value || []}
                    onChange={field.onChange}
                    maxFiles={5}
                    disabled={isLoading}
                  />
                )}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>
    </form>
  )
}
